import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZIndexUtils } from 'primeng/utils';
import { TranslationService } from 'src/app/i18n/translation.service';

/**
 * A full-screen window for a video call (the CRM demo call, screen support, and the guest page). It replaces a small dialog so the
 * call gets the whole screen: only a thin top bar (title, the page's own buttons, close) and the rest is the call. Theme tokens
 * only, so light and dark both work. On a phone it respects the notch and the home bar (safe areas).
 *
 * Two things make it truly full screen and on top (found 2026-10-05 when it opened behind an account window and was cut to the page column):
 *  1. While open, its host element is moved to the end of <body> (and put back when it closes or is destroyed, the same way PrimeNG's
 *     own overlays do). A "position: fixed" box inside a page area that has a transform or animation is sized to that area, not the screen.
 *  2. Its z-index comes from PrimeNG's own layer counter (ZIndexUtils, "modal" layer), so it opens above any dialog that is already open
 *     and below anything PrimeNG opens after it (a toast, a confirm).
 *
 * Usage:
 *   <app-call-window [open]="visible" [title]="'Video call'" (closed)="onHide()">
 *     <span cwActions> ...buttons for the top bar... </span>
 *     ...the call (an iframe that fills it) and any panels...
 *   </app-call-window>
 * The body is a positioned box: an iframe with {position:absolute; inset:0; width:100%; height:100%} fills it exactly.
 */
@Component({
  selector: 'app-call-window',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="cw" #cw *ngIf="open" role="dialog" aria-modal="true" [attr.aria-label]="title">
      <!-- Focus: hides the top bar so the call gets the whole screen (the browser's own full screen is not available on iPhone) -->
      <button type="button" class="cw-focus" [class.on]="focus" (click)="focus = !focus"
              [attr.aria-label]="focusLabel" [title]="focusLabel">
        <i class="pi" [ngClass]="focus ? 'pi-window-minimize' : 'pi-window-maximize'"></i>
      </button>
      <header class="cw-bar" *ngIf="!focus">
        <span class="cw-title">{{ title }}</span>
        <span class="cw-sub" *ngIf="subtitle">{{ subtitle }}</span>
        <span class="cw-spacer"></span>
        <span class="cw-actions"><ng-content select="[cwActions]"></ng-content></span>
        <button type="button" class="cw-x" *ngIf="closable" (click)="closed.emit()" aria-label="Close" title="Close">
          <i class="pi pi-times"></i>
        </button>
      </header>
      <div class="cw-body"><ng-content></ng-content></div>
    </div>
  `,
  styles: [`
    .cw {
      position: fixed; inset: 0; display: flex; flex-direction: column;
      background: var(--surface-ground); color: var(--text-color);
      padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right);
      animation: cwIn 0.18s ease both;
    }
    @keyframes cwIn { from { opacity: 0; } to { opacity: 1; } }
    .cw-bar {
      flex: 0 0 auto; height: 44px; display: flex; align-items: center; gap: 0.5rem; padding: 0 0.5rem 0 0.9rem;
      background: var(--surface-card); border-bottom: 1px solid var(--surface-border);
    }
    .cw-title { font-weight: 700; font-size: 0.95rem; white-space: nowrap; }
    .cw-sub { font-size: 0.78rem; color: var(--text-color-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
    .cw-spacer { flex: 1 1 auto; }
    .cw-actions { display: inline-flex; align-items: center; gap: 0.3rem; flex: 0 0 auto; }
    .cw-x {
      flex: 0 0 auto; width: 34px; height: 34px; border-radius: 10px; border: 0; cursor: pointer;
      background: transparent; color: var(--text-color-secondary); display: inline-flex; align-items: center; justify-content: center;
    }
    .cw-x:hover { background: var(--surface-hover); color: var(--text-color); }
    .cw-focus {
      position: absolute; z-index: 5; right: 0.5rem; bottom: 0.7rem; width: 40px; height: 40px; border-radius: 50%; border: 1px solid var(--surface-border);
      background: var(--surface-card); color: var(--text-color); cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25); opacity: 0.85; margin-bottom: env(safe-area-inset-bottom);
    }
    .cw-focus:hover, .cw-focus.on { opacity: 1; }
    .cw-body { position: relative; flex: 1 1 auto; min-height: 0; background: var(--surface-ground); }
    @media (max-width: 575px) { .cw-sub { display: none; } }
    @media (prefers-reduced-motion: reduce) { .cw { animation: none; } }
  `],
})
export class CallWindowComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() title = '';
  @Input() subtitle = '';
  /** False hides the close button (the guest page has no window to close). */
  @Input() closable = true;
  @Output() closed = new EventEmitter<void>();

  /** Focus mode: the top bar is hidden. Back to normal each time the window opens. */
  focus = false;

  private origParent: Node | null = null;
  private origNext: Node | null = null;
  private layered: HTMLElement | null = null;

  constructor(private host: ElementRef<HTMLElement>, private i18n: TranslationService) {}

  get focusLabel(): string {
    return this.i18n.instant(this.focus ? 'cw.focusOff' : 'cw.focusOn');
  }

  /** The window box exists only while open: give it PrimeNG's next layer when it appears, release the layer when it goes. */
  @ViewChild('cw') set windowBox(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement ?? null;
    if (el === this.layered) return;
    if (this.layered) {
      try { ZIndexUtils.clear(this.layered); } catch { /* the element is already gone */ }
      this.layered = null;
    }
    if (el) {
      ZIndexUtils.set('modal', el, 1100);
      this.layered = el;
    }
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (!ch['open']) return;
    this.focus = false;
    if (this.open) this.moveToBody(); else this.putBack();
  }

  ngOnDestroy(): void {
    this.putBack();
    if (this.layered) {
      try { ZIndexUtils.clear(this.layered); } catch { /* ignore */ }
      this.layered = null;
    }
  }

  private moveToBody(): void {
    const el = this.host.nativeElement;
    if (!el.parentNode || el.parentNode === document.body) return;
    this.origParent = el.parentNode;
    this.origNext = el.nextSibling;
    document.body.appendChild(el);
  }

  /** Back to where Angular put it, so Angular can remove it normally when its parent goes away. */
  private putBack(): void {
    const el = this.host.nativeElement;
    if (!this.origParent) return;
    const next = this.origNext && this.origNext.parentNode === this.origParent ? this.origNext : null;
    this.origParent.insertBefore(el, next);
    this.origParent = null;
    this.origNext = null;
  }
}
